import { useEffect, useState } from 'react';
import {
    Button,
    InputLabel,
    MenuItem,
    FormControl,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    IconButton,
    Box,
    Alert,
    AlertTitle,
    Typography,
    Skeleton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';

const tk = new TimekeeperService();

const ALL_FILTERS = 'all';

const getFilterKey = (type, id) => `${type}:${String(id)}`;

const ConfigCombinations = ({ open, setOpenAsignar, select, rows, reloading }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [name, setName] = useState('');
    const [combis, setCombis] = useState([]);
    const [rowsData, setRowsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState(ALL_FILTERS);
    const [filteredRows, setFilteredRows] = useState([]);
    const [alert, setAlert] = useState(false);
    const rowsCount = 5;

    // Get type color once and memoize the function
    const getTypeColor = (rowType) => {
        switch (rowType) {
            case 'Zone':
                return 'primary';
            case 'OvertimeRule':
                return 'warning';
            default:
                return 'info';
        }
    };

    // Clean up form state
    const cleanForm = () => {
        setName('');
        setCombis([]);
        setRowsData([]);
        setLoading(false);
        setFilters([]);
        setSelectedFilter(ALL_FILTERS);
        setFilteredRows([]);
        setOpenAsignar(false);
        setAlert(false);
    };

    // Check for unsaved changes
    const checkChanges = () => {
        const hasChanges = rowsData.some((r) => r.payment_method !== null) || combis.some((c) => c.combination_rule_id !== (select?.combination_rule_id || null));
        hasChanges ? setAlert(true) : cleanForm();
    };

    // Handle row updates more efficiently
    const handleRowUpdate = (rowId, value) => {
        const newValue = value === 'unique' ? null : value;

        setRowsData((prevRows) => prevRows.map((r) => (r.id === rowId ? { ...r, payment_method: newValue } : r)));
    };

    // Filter rows based on selected filter
    useEffect(() => {
        if (selectedFilter === ALL_FILTERS) {
            // Show all rows
            setFilteredRows(rowsData);
        } else {
            // Filter by selected canonical key: type:id
            setFilteredRows(
                rowsData.filter((row) => {
                    const keyA = getFilterKey(row.type_a, row.a.id);
                    const keyB = getFilterKey(row.type_b, row.b.id);

                    return keyA === selectedFilter || keyB === selectedFilter;
                }),
            );
        }
    }, [selectedFilter, rowsData]);

    useEffect(() => {
        if (open && select) {
            setLoading(true);
            setName(select.name);
            tk.combination_rule
                .getCombinationPares(select.id)
                .then((response) => {
                    const processedRows = response.map((r) => ({
                        ...r,
                        payment_method: JSON.stringify(r.payment_method) === JSON.stringify(r.a) ? 'a' : JSON.stringify(r.payment_method) === JSON.stringify(r.b) ? 'b' : null,
                    }));

                    // Extract unique elements for filters
                    const elements = [];
                    processedRows.forEach((row) => {
                        elements.push({
                            key: getFilterKey(row.type_a, row.a.id),
                            id: row.a.id,
                            type: row.type_a,
                            name: row.a.name,
                        });
                        elements.push({
                            key: getFilterKey(row.type_b, row.b.id),
                            id: row.b.id,
                            type: row.type_b,
                            name: row.b.name,
                        });
                    });

                    // Create unique filters based on canonical key: type:id
                    const uniqueFilters = [];
                    const seenKeys = new Set();

                    elements.forEach((element) => {
                        if (!seenKeys.has(element.key)) {
                            seenKeys.add(element.key);
                            uniqueFilters.push(element);
                        }
                    });

                    setFilters(uniqueFilters);
                    setRowsData(processedRows);
                    setFilteredRows(processedRows); // Initially show all rows
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching combination pairs:', error);
                    setLoading(false);
                });
        }
    }, [open, select, rows]);

    const asignarCombinationRules = async (items) => {
        setLoading(true);
        const response = await tk.combination_rule.asignarCombination(items);

        if (response.status === 200) {
            enqueueSnackbar(t('settings.combinations.dialog.alerts.combination_configuration.success'), {
                variant: 'success',
            });
            cleanForm();
            reloading();
        }

        if (response.status !== 200) {
            enqueueSnackbar(t('settings.combinations.dialog.alerts.combination_configuration.error'), {
                variant: 'error',
            });
            setOpenAsignar(false);
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog
                maxWidth="md"
                fullWidth
                open={open}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') return;
                    checkChanges();
                }}
            >
                <DialogTitle>{t('settings.combinations.dialog.titles.combination_configuration')}</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={checkChanges}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                    {alert && (
                        <Alert
                            variant="filled"
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                                <>
                                    <Button onClick={() => setAlert(false)} color="inherit" size="small" variant="text" disableElevation>
                                        {t('general.alerts.unsaved_changes.buttons.yes')}
                                    </Button>
                                    <Button onClick={() => cleanForm()} color="inherit" size="small" variant="text" disableElevation>
                                        {t('general.alerts.unsaved_changes.buttons.no')}
                                    </Button>
                                </>
                            }
                        >
                            <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                        </Alert>
                    )}
                </Box>

                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('settings.combinations.dialog.combination_configuration.name')}
                        {`: ${name}`}
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel id="filter-select-label">{t('settings.combinations.dialog.combination_configuration.combinations_elements')}</InputLabel>
                        <Select
                            disabled={loading}
                            labelId="filter-select-label"
                            id="filter-select"
                            value={selectedFilter}
                            label={t('settings.combinations.dialog.combination_configuration.combinations_elements')}
                            onChange={(e) => {
                                setSelectedFilter(String(e.target.value));
                            }}
                        >
                            <MenuItem key="select-all" value={ALL_FILTERS}>
                                {t('settings.combinations.dialog.combination_configuration.all_combinations')}
                            </MenuItem>
                            {filters.map((filter) => (
                                <MenuItem key={`filter-${filter.key}`} value={filter.key}>
                                    {`${filter.name} (${filter.type})`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* <LoadingBar loading={loading} /> */}

                    {!loading ? (
                        <TableContainer sx={{ mt: 2 }} component={Paper}>
                            <Table stickyHeader sx={{ minWidth: 650 }} aria-label="combinations table" size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('settings.combinations.dialog.titles.combination_configuration')}</TableCell>
                                        <TableCell align="right">{t('settings.combinations.dialog.combination_configuration.mode_of_remuneration')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows.length > 0 ? (
                                        filteredRows.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Chip label={row.a.name} color={getTypeColor(row.type_a)} size="small" variant="outlined" />
                                                        <Chip label={row.b.name} color={getTypeColor(row.type_b)} size="small" variant="outlined" />
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel id={`remuneration-label-${row.id}`}>{t('settings.combinations.dialog.combination_configuration.mode_of_remuneration')}</InputLabel>
                                                        <Select
                                                            labelId={`remuneration-label-${row.id}`}
                                                            id={`remuneration-select-${row.id}`}
                                                            value={row?.payment_method || 'unique'}
                                                            label={t('settings.combinations.dialog.combination_configuration.mode_of_remuneration')}
                                                            onChange={(e) => handleRowUpdate(row.id, e.target.value)}
                                                        >
                                                            <MenuItem value="unique">{t('settings.combinations.dialog.combination_configuration.unique')}</MenuItem>
                                                            <MenuItem value={'a'}>{row.a.name}</MenuItem>
                                                            <MenuItem value={'b'}>{row.b.name}</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={2} align="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                    {t('settings.combinations.dialog.combination_configuration.no_combinations_found') || 'No combinations found'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <TableContainer sx={{ mt: 2 }} component={Paper}>
                            <Table stickyHeader sx={{ minWidth: 650 }} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('settings.combinations.dialog.combination_configuration.title')}</TableCell>
                                        <TableCell align="right">{t('settings.combinations.dialog.combination_configuration.mode_of_remuneration')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array(rowsCount)
                                        .fill(0)
                                        .map((_, index) => (
                                            <TableRow key={`skeleton-row-${index}`}>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Skeleton variant="rounded" width={80} height={24} />
                                                        <Skeleton variant="rounded" width={80} height={24} />
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Skeleton variant="rounded" width="100%" height={40} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={checkChanges} color="error" variant="contained">
                        {t('settings.combinations.dialog.buttons.close')}
                    </Button>
                    <Button onClick={() => asignarCombinationRules(rowsData)} color="primary" variant="contained" disabled={loading} loading={loading}>
                        {t('settings.combinations.dialog.buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { ConfigCombinations };
